import { forwardRef } from "react";
import { createPolymorphicComponent, type PolymorphicFactory } from "@mantine/core";
import { type PolymorphicFactoryPayload } from "@mantine/core/lib/core/factory/polymorphic-factory";

/**
 * More information about Mantine's polymorphic components can be found here:
 * https://mantine.dev/guides/polymorphic/#wrapping-polymorphic-components
 * @param displayName Display name for the component, used for debugging purposes
 * @param render Render function for the component
 * @returns Polymorphic wrapped component
 */
export const polymorphicComponentWrapper = <ElementType extends PolymorphicFactory<PolymorphicFactoryPayload>, Props>(
    displayName: string,
    render: React.ForwardRefRenderFunction<ElementType["defaultRef"], React.PropsWithoutRef<Props>>
) => {
    // First we create the forwardRef component
    const ForwardedComponent = forwardRef<ElementType["defaultRef"], Props>(render) as React.ForwardRefExoticComponent<
        React.PropsWithoutRef<Props> & React.RefAttributes<ElementType["ref"]>
    > &
        ElementType["staticComponents"];
    // Then we set the display name for debugging purposes
    if (displayName) {
        ForwardedComponent.displayName = displayName;
    }
    // Finally, we create the polymorphic component using Mantine's utility
    return createPolymorphicComponent<ElementType["defaultComponent"], Props, ElementType["staticComponents"]>(
        ForwardedComponent
    );
};

/**
 * Generic version of polymorphicComponentWrapper for components that need to support generic types.
 * This is a simplified approach that creates a factory function for generic polymorphic components.
 * @returns Function that creates polymorphic wrapped components with specific generic types
 */
export const createGenericPolymorphicComponentWrapper = () => {
    return <ElementType extends PolymorphicFactory<PolymorphicFactoryPayload>, Props>(
        displayName: string,
        render: React.ForwardRefRenderFunction<ElementType["defaultRef"], React.PropsWithoutRef<Props>>
    ) => {
        // First we create the forwardRef component
        const ForwardedComponent = forwardRef<ElementType["defaultRef"], Props>(
            render
        ) as React.ForwardRefExoticComponent<React.PropsWithoutRef<Props> & React.RefAttributes<ElementType["ref"]>> &
            ElementType["staticComponents"];

        // Then we set the display name for debugging purposes
        if (displayName) {
            ForwardedComponent.displayName = displayName;
        }

        // Finally, we create the polymorphic component using Mantine's utility
        return createPolymorphicComponent<ElementType["defaultComponent"], Props, ElementType["staticComponents"]>(
            ForwardedComponent
        );
    };
};

export default polymorphicComponentWrapper;
